import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { listDriverDocuments, updateDriverDocument } from '../../services/driverDocumentsService';
import { listVehicleDocuments, getVehicles } from '../../services/vehiclesService';
import { getDrivers } from '../../services/driversService';
import api from '../../config/api';
import './DocumentManagement.css';

// Base document types - will be extended with types from API
const BASE_DOCUMENT_TYPES = ['RC Book', 'Insurance Policy', 'Driving License', 'Permit', 'PAN Card', 'Aadhar Card', 'Fitness Certificate', 'Pollution Certificate', 'Road Tax', 'Medical Certificate', 'PUC', 'Insurance Certificate'];

const DocumentManagement = () => {
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [documentTypeDropdownOpen, setDocumentTypeDropdownOpen] = useState(false);
  const [entityTypeDropdownOpen, setEntityTypeDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [documentTypeSearch, setDocumentTypeSearch] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replacingDocument, setReplacingDocument] = useState(null);
  const [newDocumentFile, setNewDocumentFile] = useState(null);
  const [newDocumentPreview, setNewDocumentPreview] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [showReplaceSuccess, setShowReplaceSuccess] = useState(false);
  const [replacedDocumentType, setReplacedDocumentType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documents, setDocuments] = useState([]);

  const documentTypeDropdownRef = useRef(null);
  const entityTypeDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);

  // Mock data - kept for fallback or initial state
  const [mockDocuments, setMockDocuments] = useState([
    // Vehicle Documents
    {
      id: 'DOC-000456',
      documentType: 'RC Book',
      entityName: 'KA01AB1234',
      entityType: 'Vehicle',
      documentNumber: 'KA0120220005678',
      lastUpdated: '10 Jan 2023, 14:30',
      validTill: '09 Jan 2024',
      status: 'Valid',
      entityId: 'VHC0001',
      fileName: 'RC_Book_KA01AB1234.pdf',
      fileSize: '1.2 MB',
      uploadDate: '10 Jan 2023',
      preview: 'https://via.placeholder.com/800x1000/6366F1/FFFFFF?text=RC+Book+Document'
    },
    {
      id: 'DOC-000457',
      documentType: 'Insurance Policy',
      entityName: 'KA01AB1234',
      entityType: 'Vehicle',
      documentNumber: 'INS098765432',
      lastUpdated: '15 Apr 2023, 10:15',
      validTill: '04 Feb 2028',
      status: 'Valid',
      entityId: 'VHC0001',
      fileName: 'Insurance_KA01AB1234.pdf',
      fileSize: '0.8 MB',
      uploadDate: '15 Apr 2023',
      preview: 'https://via.placeholder.com/800x1000/10B981/FFFFFF?text=Insurance+Policy'
    },
    {
      id: 'DOC-000459',
      documentType: 'Permit',
      entityName: 'KA01AB1234',
      entityType: 'Vehicle',
      documentNumber: 'PER123456789',
      lastUpdated: '20 Apr 2023',
      validTill: '25 Apr 2023',
      status: 'Expired',
      entityId: 'VHC0001'
    },
    {
      id: 'DOC-000462',
      documentType: 'Fitness Certificate',
      entityName: 'MH12AB1234',
      entityType: 'Vehicle',
      documentNumber: 'FIT987654321',
      lastUpdated: '01 May 2023',
      validTill: '25 Apr 2023',
      status: 'Expiring Soon',
      entityId: 'VHC0002'
    },
    // Driver Documents
    {
      id: 'DOC-000458',
      documentType: 'Driving License',
      entityName: 'Rajesh Kumar',
      entityType: 'Driver',
      documentNumber: 'DL5678901234',
      lastUpdated: '12 Mar 2023, 09:45',
      validTill: '15 Jun 2025',
      status: 'Valid',
      entityId: 'DRV0001',
      fileName: 'Driving_License_Rajesh_Kumar.pdf',
      fileSize: '0.5 MB',
      uploadDate: '12 Mar 2023',
      preview: 'https://via.placeholder.com/800x1000/2563EB/FFFFFF?text=Driving+License'
    },
    {
      id: 'DOC-000461',
      documentType: 'Aadhar Card',
      entityName: 'Rajesh Kumar',
      entityType: 'Driver',
      documentNumber: '1234 5678 9012',
      lastUpdated: '05 Jan 2023',
      validTill: '-',
      status: 'Valid',
      entityId: 'DRV0001'
    },
    {
      id: 'DOC-000463',
      documentType: 'PAN Card',
      entityName: 'Amit Singh',
      entityType: 'Driver',
      documentNumber: 'ABCDE1234F',
      lastUpdated: '08 Feb 2023',
      validTill: '-',
      status: 'Valid',
      entityId: 'DRV0002'
    },
    {
      id: 'DOC-000464',
      documentType: 'Medical Certificate',
      entityName: 'Amit Singh',
      entityType: 'Driver',
      documentNumber: 'MED987654321',
      lastUpdated: '10 May 2023',
      validTill: '10 Nov 2023',
      status: 'Expiring Soon',
      entityId: 'DRV0002'
    }
  ]);

  const entityTypes = ['Vehicle', 'Driver'];
  const statusTypes = ['Valid', 'Expired', 'Expiring Soon'];

  // Get unique document types from actual documents (dynamically populated)
  const documentTypes = useMemo(() => {
    const typesFromDocs = new Set(BASE_DOCUMENT_TYPES);
    documents.forEach(doc => {
      if (doc.documentType && doc.documentType !== 'Unknown') {
        typesFromDocs.add(doc.documentType);
      }
    });
    return Array.from(typesFromDocs).sort();
  }, [documents]);

  // Map API document types to standardized names
  const normalizeDocumentType = (apiDocType) => {
    if (!apiDocType) return 'Unknown';
    
    const normalized = String(apiDocType).trim();
    
    // Map common variations to standard names
    const typeMap = {
      'rc book': 'RC Book',
      'rc_book': 'RC Book',
      'rcbook': 'RC Book',
      'insurance policy': 'Insurance Policy',
      'insurance_policy': 'Insurance Policy',
      'insurancepolicy': 'Insurance Policy',
      'insurance certificate': 'Insurance Certificate',
      'insurance_certificate': 'Insurance Certificate',
      'insurancecertificate': 'Insurance Certificate',
      'driving license': 'Driving License',
      'driving_license': 'Driving License',
      'drivinglicense': 'Driving License',
      'permit': 'Permit',
      'pan card': 'PAN Card',
      'pan_card': 'PAN Card',
      'pancard': 'PAN Card',
      'aadhar card': 'Aadhar Card',
      'aadhar_card': 'Aadhar Card',
      'aadharcard': 'Aadhar Card',
      'fitness certificate': 'Fitness Certificate',
      'fitness_certificate': 'Fitness Certificate',
      'fitnesscertificate': 'Fitness Certificate',
      'pollution certificate': 'Pollution Certificate',
      'pollution_certificate': 'Pollution Certificate',
      'pollutioncertificate': 'Pollution Certificate',
      'puc': 'PUC',
      'road tax': 'Road Tax',
      'road_tax': 'Road Tax',
      'roadtax': 'Road Tax',
      'medical certificate': 'Medical Certificate',
      'medical_certificate': 'Medical Certificate',
      'medicalcertificate': 'Medical Certificate'
    };
    
    const lowerKey = normalized.toLowerCase();
    return typeMap[lowerKey] || normalized; // Return mapped value or original if no mapping found
  };

  // Helper function to calculate document status based on expiry date
  const calculateStatus = (expiryDate) => {
    if (!expiryDate || expiryDate === '-' || expiryDate === '') return 'Valid';
    
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return 'Expired';
      if (diffDays <= 15) return 'Expiring Soon';
      return 'Valid';
    } catch (e) {
      return 'Valid';
    }
  };

  // Helper function to format date for display
  const formatDate = (dateString) => {
    if (!dateString || dateString === '-' || dateString === '') return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  // Helper function to format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const timePart = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      return `${datePart}, ${timePart}`;
    } catch (e) {
      return dateString;
    }
  };

  // Transform API document to component format
  const transformDocument = (apiDoc, entityType, entityName, entityId) => {
    const expiryDate = apiDoc.expiry_date || apiDoc.expiryDate || '';
    const status = calculateStatus(expiryDate);
    const rawDocType = apiDoc.document_type || apiDoc.documentType || 'Unknown';
    const normalizedDocType = normalizeDocumentType(rawDocType);
    const dbId = apiDoc.id || apiDoc.document_id || '';
    
    // Create unique ID that includes entity type to avoid collisions between driver_documents and vehicle_documents
    const entityPrefix = entityType === 'Driver' ? 'DRV' : 'VHC';
    const uniqueId = `${entityPrefix}-${String(dbId).padStart(6, '0')}`;
    
    return {
      id: uniqueId,
      displayId: `DOC-${String(dbId).padStart(6, '0')}`, // Keep original format for display
      documentType: normalizedDocType,
      entityName: entityName || 'Unknown',
      entityType: entityType,
      documentNumber: apiDoc.document_number || apiDoc.documentNumber || '-',
      lastUpdated: formatDateTime(apiDoc.updated_at || apiDoc.updatedAt || apiDoc.created_at || apiDoc.createdAt),
      validTill: formatDate(expiryDate),
      status: status,
      entityId: entityId || '',
      fileName: apiDoc.file_name || apiDoc.fileName || '',
      fileSize: apiDoc.file_size ? `${(apiDoc.file_size / (1024 * 1024)).toFixed(2)} MB` : '',
      uploadDate: formatDate(apiDoc.created_at || apiDoc.createdAt),
      preview: apiDoc.document_url || apiDoc.documentUrl || null,
      rawData: apiDoc, // Keep raw data for updates
      dbId: dbId, // Keep original database ID
      entityPrefix: entityPrefix // Keep entity prefix for reference
    };
  };

  // Fetch documents from APIs
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch documents from both APIs in parallel
      const [driverDocsResponse, vehicleDocsResponse] = await Promise.all([
        listDriverDocuments({ limit: 1000, offset: 0 }).catch(err => {
          console.warn('Failed to fetch driver documents:', err);
          return { data: [], items: [] };
        }),
        listVehicleDocuments({ limit: 1000, offset: 0 }).catch(err => {
          console.warn('Failed to fetch vehicle documents:', err);
          return { data: [], items: [] };
        })
      ]);

      // Extract documents from responses
      let driverDocs = [];
      let vehicleDocs = [];
      
      if (Array.isArray(driverDocsResponse)) {
        driverDocs = driverDocsResponse;
      } else if (driverDocsResponse?.data && Array.isArray(driverDocsResponse.data)) {
        driverDocs = driverDocsResponse.data;
      } else if (driverDocsResponse?.items && Array.isArray(driverDocsResponse.items)) {
        driverDocs = driverDocsResponse.items;
      }

      if (Array.isArray(vehicleDocsResponse)) {
        vehicleDocs = vehicleDocsResponse;
      } else if (vehicleDocsResponse?.data && Array.isArray(vehicleDocsResponse.data)) {
        vehicleDocs = vehicleDocsResponse.data;
      } else if (vehicleDocsResponse?.items && Array.isArray(vehicleDocsResponse.items)) {
        vehicleDocs = vehicleDocsResponse.items;
      }

      // Fetch vehicles and drivers to map IDs to names
      const [vehiclesResponse, driversResponse] = await Promise.all([
        getVehicles({ limit: 1000, offset: 0 }).catch(err => {
          console.warn('Failed to fetch vehicles for mapping:', err);
          return { data: [], items: [] };
        }),
        getDrivers({ limit: 1000, offset: 0 }).catch(err => {
          console.warn('Failed to fetch drivers for mapping:', err);
          return { data: [], items: [] };
        })
      ]);

      // Extract vehicles and drivers (handle various response structures)
      let vehicles = [];
      let drivers = [];
      
      if (Array.isArray(vehiclesResponse)) {
        vehicles = vehiclesResponse;
      } else if (vehiclesResponse?.data?.items && Array.isArray(vehiclesResponse.data.items)) {
        vehicles = vehiclesResponse.data.items;
      } else if (vehiclesResponse?.data && Array.isArray(vehiclesResponse.data)) {
        vehicles = vehiclesResponse.data;
      } else if (vehiclesResponse?.items && Array.isArray(vehiclesResponse.items)) {
        vehicles = vehiclesResponse.items;
      }

      if (Array.isArray(driversResponse)) {
        drivers = driversResponse;
      } else if (driversResponse?.data?.items && Array.isArray(driversResponse.data.items)) {
        drivers = driversResponse.data.items;
      } else if (driversResponse?.data && Array.isArray(driversResponse.data)) {
        drivers = driversResponse.data;
      } else if (driversResponse?.items && Array.isArray(driversResponse.items)) {
        drivers = driversResponse.items;
      }

      console.log('Fetched drivers for mapping:', drivers.length, drivers.slice(0, 3));
      console.log('Fetched driver documents:', driverDocs.length, driverDocs.slice(0, 3));
      console.log('Sample driver document types:', driverDocs.slice(0, 5).map(d => d.document_type || d.documentType));
      console.log('Sample vehicle document types:', vehicleDocs.slice(0, 5).map(d => d.document_type || d.documentType));

      // Create maps for quick lookup (normalize IDs to strings for consistent matching)
      const vehicleMap = new Map();
      vehicles.forEach(v => {
        const id = v.id || v.vehicle_id;
        if (id !== undefined && id !== null) {
          // Store both string and number versions for lookup
          const idStr = String(id);
          const idNum = Number(id);
          const vehicleName = v.vehicle_number || v.vehicleNumber || `Vehicle #${id}`;
          vehicleMap.set(idStr, vehicleName);
          if (!isNaN(idNum)) {
            vehicleMap.set(idNum, vehicleName);
          }
        }
      });

      const driverMap = new Map();
      drivers.forEach(d => {
        const id = d.id || d.driver_id;
        if (id !== undefined && id !== null) {
          // Store both string and number versions for lookup
          const idStr = String(id);
          const idNum = Number(id);
          const driverName = d.full_name || d.fullName || d.name || `Driver #${id}`;
          driverMap.set(idStr, driverName);
          if (!isNaN(idNum)) {
            driverMap.set(idNum, driverName);
          }
        }
      });

      // Transform documents
      const transformedDocs = [
        ...driverDocs.map(doc => {
          const driverId = doc.driver_id || doc.driverId;
          if (driverId !== undefined && driverId !== null) {
            // Try both string and number lookups
            const driverName = driverMap.get(String(driverId)) || 
                              driverMap.get(Number(driverId)) || 
                              `Driver #${driverId}`;
            return transformDocument(doc, 'Driver', driverName, driverId);
          }
          return transformDocument(doc, 'Driver', 'Unknown Driver', driverId);
        }),
        ...vehicleDocs.map(doc => {
          const vehicleId = doc.vehicle_id || doc.vehicleId;
          if (vehicleId !== undefined && vehicleId !== null) {
            // Try both string and number lookups
            const vehicleName = vehicleMap.get(String(vehicleId)) || 
                               vehicleMap.get(Number(vehicleId)) || 
                               `Vehicle #${vehicleId}`;
            return transformDocument(doc, 'Vehicle', vehicleName, vehicleId);
          }
          return transformDocument(doc, 'Vehicle', 'Unknown Vehicle', vehicleId);
        })
      ];

      // Deduplicate documents based on unique key (entityType + dbId + documentNumber)
      // This prevents the same document from appearing multiple times
      const seenDocs = new Map();
      const deduplicatedDocs = transformedDocs.filter(doc => {
        // Create a unique key: entityType + dbId + documentNumber + documentType
        const uniqueKey = `${doc.entityType}-${doc.dbId}-${doc.documentNumber}-${doc.documentType}`;
        
        // If we've seen this exact document before, skip it
        if (seenDocs.has(uniqueKey)) {
          console.warn('Duplicate document detected and removed:', uniqueKey, doc);
          return false;
        }
        
        seenDocs.set(uniqueKey, true);
        return true;
      });

      console.log('Total documents before deduplication:', transformedDocs.length);
      console.log('Total documents after deduplication:', deduplicatedDocs.length);

      setDocuments(deduplicatedDocs);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.message || 'Failed to fetch documents. Please try again.');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch documents on mount
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Filter document types based on search
  const filteredDocumentTypes = useMemo(() => {
    if (!documentTypeSearch) return documentTypes;
    return documentTypes.filter(type => 
      type.toLowerCase().includes(documentTypeSearch.toLowerCase())
    );
  }, [documentTypeSearch]);

  // Calculate expiring documents count
  const expiringDocumentsCount = useMemo(() => {
    const today = new Date();
    const fifteenDaysLater = new Date(today);
    fifteenDaysLater.setDate(today.getDate() + 15);
    
    return documents.filter(doc => {
      if (doc.validTill === '-' || doc.status === 'Expired') return false;
      try {
        const validTillDate = new Date(doc.validTill.split(' ').reverse().join(' '));
        return validTillDate <= fifteenDaysLater && validTillDate >= today;
      } catch (e) {
        return false;
      }
    }).length;
  }, [documents]);

  // Filter documents
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      // Document Type filter - normalize both sides for comparison
      const matchesDocumentType = !documentTypeFilter || 
        normalizeDocumentType(doc.documentType) === normalizeDocumentType(documentTypeFilter) ||
        doc.documentType === documentTypeFilter;
      
      // Entity Type filter - exact match
      const matchesEntityType = !entityTypeFilter || doc.entityType === entityTypeFilter;
      
      // Status filter - exact match (status is already normalized)
      const matchesStatus = !statusFilter || doc.status === statusFilter;
      
      // Search filter - case-insensitive partial match
      const searchLower = searchQuery.toLowerCase().trim();
      const matchesSearch = !searchLower || 
        (doc.documentType &&         doc.documentType.toLowerCase().includes(searchLower)) ||
        (doc.entityName && doc.entityName.toLowerCase().includes(searchLower)) ||
        (doc.documentNumber && doc.documentNumber.toLowerCase().includes(searchLower)) ||
        (doc.displayId && doc.displayId.toLowerCase().includes(searchLower)) ||
        (doc.id && doc.id.toLowerCase().includes(searchLower));
      
      return matchesDocumentType && matchesEntityType && matchesStatus && matchesSearch;
    });
  }, [documents, documentTypeFilter, entityTypeFilter, statusFilter, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDocuments = filteredDocuments.slice(startIndex, endIndex);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (documentTypeDropdownRef.current && !documentTypeDropdownRef.current.contains(event.target)) {
        setDocumentTypeDropdownOpen(false);
      }
      if (entityTypeDropdownRef.current && !entityTypeDropdownRef.current.contains(event.target)) {
        setEntityTypeDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [documentTypeFilter, entityTypeFilter, statusFilter, searchQuery, itemsPerPage]);

  // Update viewingDocument when documents changes for the same document
  useEffect(() => {
    if (viewingDocument && showViewModal) {
      const updatedDoc = documents.find(doc => doc.id === viewingDocument.id);
      if (updatedDoc) {
        // Only update if there are actual changes (check preview specifically)
        if (updatedDoc.preview !== viewingDocument.preview || 
            updatedDoc.fileName !== viewingDocument.fileName ||
            updatedDoc.fileSize !== viewingDocument.fileSize) {
          setViewingDocument(updatedDoc);
        }
      }
    }
  }, [documents, showViewModal, viewingDocument]);

  const handleExport = () => {
    // CSV Headers
    const headers = ['Document ID', 'Document Type', 'Entity Name', 'Entity Type', 'Document Number', 'Last Updated', 'Expiry Date', 'Status', 'Entity ID', 'Upload Date'];
    
    // Convert documents to CSV rows
    const csvRows = [
      headers.join(','),
      ...documents.map(doc => {
        return [
          doc.displayId || doc.id || '',
          doc.documentType || '',
          doc.entityName || '',
          doc.entityType || '',
          doc.documentNumber || '',
          doc.lastUpdated || '',
          doc.validTill || '',
          doc.status || '',
          doc.entityId || '',
          doc.uploadDate || ''
        ].map(field => {
          // Escape commas and quotes in CSV
          const fieldStr = String(field || '');
          if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
            return `"${fieldStr.replace(/"/g, '""')}"`;
          }
          return fieldStr;
        }).join(',');
      })
    ];
    
    // Create CSV content with BOM for Excel compatibility
    const csvContent = '\ufeff' + csvRows.join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `documents_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleView = (document) => {
    // Always get the latest document data from documents to ensure we show updated preview
    const latestDocument = documents.find(doc => doc.id === document.id) || document;
    setViewingDocument(latestDocument);
    setZoomLevel(100);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingDocument(null);
    setZoomLevel(100);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const handleFullscreen = () => {
    // Fullscreen functionality would go here
    alert('Fullscreen view will be implemented');
  };

  // Helper function to convert date from "DD MMM YYYY" to "YYYY-MM-DD" format
  const convertToDateInputFormat = (dateString) => {
    if (!dateString || dateString === '-') return '';
    try {
      // Parse date like "09 Jan 2024"
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  // Helper function to convert date from "YYYY-MM-DD" to "DD MMM YYYY" format
  const convertToDisplayFormat = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return '';
    }
  };

  const handleReplaceDocument = () => {
    setReplacingDocument(viewingDocument);
    // Convert expiry date to date input format if it exists
    const expiryDateValue = viewingDocument.validTill && viewingDocument.validTill !== '-' 
      ? convertToDateInputFormat(viewingDocument.validTill)
      : '';
    setExpiryDate(expiryDateValue);
    setNewDocumentFile(null);
    setNewDocumentPreview(null);
    setShowReplaceModal(true);
    setShowViewModal(false);
  };

  const handleCloseReplaceModal = () => {
    setShowReplaceModal(false);
    setReplacingDocument(null);
    setNewDocumentFile(null);
    setNewDocumentPreview(null);
    setExpiryDate('');
    setIsDragging(false);
  };

  const handleFileSelect = (file) => {
    if (file) {
      setNewDocumentFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setNewDocumentPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        // For PDFs and other files, create a data URL preview using FileReader
        // This allows instant preview in the View modal after upload
        const reader = new FileReader();
        reader.onloadend = () => {
          // For PDFs, we can use a placeholder or convert to image
          // For now, use a placeholder that will be replaced with actual preview
          setNewDocumentPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleReplaceAndSave = () => {
    if (!newDocumentFile) {
      alert('Please select a file to replace');
      return;
    }
    // Show confirmation popup
    setShowReplaceConfirm(true);
  };

  const handleReplaceConfirm = async () => {
    if (!replacingDocument || !newDocumentFile) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Get the original database ID from the document
      const documentId = replacingDocument.dbId;
      if (!documentId) {
        throw new Error('Document ID not found');
      }
      
      // Create FormData for the API call
      const formData = new FormData();
      
      // Determine if it's a driver or vehicle document and use appropriate field name
      if (replacingDocument.entityType === 'Driver') {
        formData.append('file', newDocumentFile);
      } else if (replacingDocument.entityType === 'Vehicle') {
        formData.append('document', newDocumentFile); // Vehicle documents use 'document' field
      }
      
      // Add expiry date if provided
      if (expiryDate) {
        // Convert to YYYY-MM-DD format for API
        const expiryDateFormatted = expiryDate.includes('-') && expiryDate.length === 10 
          ? expiryDate 
          : convertToDateInputFormat(expiryDate);
        if (expiryDateFormatted) {
          // Convert to ISO string for vehicle documents, keep YYYY-MM-DD for driver documents
          if (replacingDocument.entityType === 'Vehicle') {
            formData.append('expiry_date', new Date(expiryDateFormatted).toISOString());
          } else {
            formData.append('expiry_date', expiryDateFormatted);
          }
        }
      }
      
      // Add document type if available
      if (replacingDocument.documentType) {
        // Convert display format back to API format (e.g., "RC Book" -> "rc_book")
        const docType = replacingDocument.documentType.toLowerCase().replace(/\s+/g, '_');
        formData.append('document_type', docType);
      }
      
      // Determine if it's a driver or vehicle document and call appropriate API
      let response;
      if (replacingDocument.entityType === 'Driver') {
        // Update driver document
        response = await updateDriverDocument(documentId, formData);
      } else if (replacingDocument.entityType === 'Vehicle') {
        // Update vehicle document - use PUT /api/vehicles/documents/{documentId}
        response = await api.put(`/api/vehicles/documents/${documentId}`, formData, {
          headers: { Accept: 'application/json' }
        });
        response = response.data || response;
      } else {
        throw new Error('Unknown entity type');
      }
      
      // Refresh documents list after successful update
      await fetchDocuments();
      
      // Close modals and show success
      setReplacedDocumentType(replacingDocument?.documentType || '');
      setShowReplaceConfirm(false);
      handleCloseReplaceModal();
      setShowReplaceSuccess(true);
      
    } catch (error) {
      console.error('Error replacing document:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to replace document. Please try again.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceCancel = () => {
    setShowReplaceConfirm(false);
  };

  const handleReplaceSuccessClose = () => {
    setShowReplaceSuccess(false);
    setReplacedDocumentType('');
  };

  const handleDownload = (document) => {
    alert(`Downloading document: ${document.id}`);
  };

  const handleUpload = (document) => {
    alert(`Uploading new document for: ${document.id}`);
  };

  return (
    <div className="document-management-container">
      <div className="document-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <div className="document-filters" style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div className="filter-group" style={{ display: 'flex', gap: '12px' }}>
          <div className="filter-wrapper" ref={documentTypeDropdownRef}>
            <button
              className={`filter-btn ${documentTypeFilter ? 'active' : ''}`}
              onClick={() => {
                setDocumentTypeDropdownOpen(!documentTypeDropdownOpen);
                setEntityTypeDropdownOpen(false);
                setStatusDropdownOpen(false);
                if (!documentTypeDropdownOpen) {
                  setDocumentTypeSearch('');
                }
              }}
            >
              <span>📄</span> Document Type {documentTypeFilter && `(${documentTypeFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {documentTypeDropdownOpen && (
              <div className="filter-dropdown">
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                  <input
                    type="text"
                    placeholder="Search document type..."
                    value={documentTypeSearch}
                    onChange={(e) => setDocumentTypeSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.target.style.borderColor = '#2563EB'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      outline: 'none'
                    }}
                  />
                </div>
                <div 
                  className="filter-option" 
                  onClick={() => {
                    setDocumentTypeFilter('');
                    setDocumentTypeDropdownOpen(false);
                    setDocumentTypeSearch('');
                  }}
                >
                  All Document Types
                </div>
                {filteredDocumentTypes.length === 0 ? (
                  <div style={{ padding: '10px 16px', fontSize: '0.9rem', color: '#9CA3AF', textAlign: 'center' }}>
                    No document types found
                  </div>
                ) : (
                  filteredDocumentTypes.map(type => (
                    <div
                      key={type}
                      className="filter-option"
                      onClick={() => {
                        setDocumentTypeFilter(type);
                        setDocumentTypeDropdownOpen(false);
                        setDocumentTypeSearch('');
                      }}
                    >
                      {type}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={entityTypeDropdownRef}>
            <button
              className={`filter-btn ${entityTypeFilter ? 'active' : ''}`}
              onClick={() => {
                setEntityTypeDropdownOpen(!entityTypeDropdownOpen);
                setDocumentTypeDropdownOpen(false);
                setStatusDropdownOpen(false);
              }}
            >
              <span>🏷️</span> Entity Type {entityTypeFilter && `(${entityTypeFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {entityTypeDropdownOpen && (
              <div className="filter-dropdown">
                <div 
                  className="filter-option" 
                  onClick={() => {
                    setEntityTypeFilter('');
                    setEntityTypeDropdownOpen(false);
                  }}
                >
                  All Entity Types
                </div>
                {entityTypes.map(type => (
                  <div
                    key={type}
                    className="filter-option"
                    onClick={() => {
                      setEntityTypeFilter(type);
                      setEntityTypeDropdownOpen(false);
                    }}
                  >
                    {type}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={statusDropdownRef}>
            <button
              className={`filter-btn ${statusFilter ? 'active' : ''}`}
              onClick={() => {
                setStatusDropdownOpen(!statusDropdownOpen);
                setDocumentTypeDropdownOpen(false);
                setEntityTypeDropdownOpen(false);
              }}
            >
              <span>✓</span> Status {statusFilter && `(${statusFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {statusDropdownOpen && (
              <div className="filter-dropdown">
                <div 
                  className="filter-option" 
                  onClick={() => {
                    setStatusFilter('');
                    setStatusDropdownOpen(false);
                  }}
                >
                  All Status
                </div>
                {statusTypes.map(status => (
                  <div
                    key={status}
                    className="filter-option"
                    onClick={() => {
                      setStatusFilter(status);
                      setStatusDropdownOpen(false);
                    }}
                  >
                    {status}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="document-actions" style={{ marginLeft: 'auto' }}>
          <button className="btn-secondary" onClick={handleExport} title="Export">
            <span>📊</span>
          </button>
        </div>
      </div>
      </div>

      {error && (
        <div className="expiry-alert" style={{ background: '#FEE2E2', color: '#991B1B', borderColor: '#FCA5A5' }}>
          <div className="alert-icon">❌</div>
          <div className="alert-message">
            {error}
            <button 
              onClick={fetchDocuments} 
              style={{ marginLeft: '10px', padding: '4px 8px', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {expiringDocumentsCount > 0 && (
        <div className="expiry-alert">
          <div className="alert-icon">⚠️</div>
          <div className="alert-message">
            {expiringDocumentsCount} document{expiringDocumentsCount !== 1 ? 's are' : ' is'} expiring within 15 days. Please review and renew them to avoid compliance issues.
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
          Loading documents...
        </div>
      )}

      {!loading && (
      <>

      <div className="table-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="show-entries" ref={showEntriesDropdownRef}>
          <span>Show</span>
          <button
            className="entries-btn"
            onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
          >
            {itemsPerPage} entries
            <span style={{ marginLeft: '8px' }}>▼</span>
          </button>
          {showEntriesDropdownOpen && (
            <div className="entries-dropdown">
              {[10, 25, 50, 100].map(num => (
                <div
                  key={num}
                  className="entries-option"
                  onClick={() => {
                    setItemsPerPage(num);
                    setShowEntriesDropdownOpen(false);
                  }}
                >
                  {num} entries
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="search-icon" style={{ fontSize: '14px' }}>🔍</div>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or document number"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ 
              height: '32px', 
              padding: '4px 12px', 
              fontSize: '0.875rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              outline: 'none'
            }}
          />
        </div>
      </div>

      <div className="document-table-container">
        <table className="document-table">
          <thead>
            <tr>
              <th>Document ID</th>
              <th>Document Type</th>
              <th>Entity Name</th>
              <th>Entity Type</th>
              <th>Document Number</th>
              <th>Last Updated</th>
              <th>Valid Till</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentDocuments.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                  No documents found
                </td>
              </tr>
            ) : (
              currentDocuments.map((doc) => (
                <tr key={doc.id}>
                  <td>{doc.displayId || doc.id}</td>
                  <td>{doc.documentType}</td>
                  <td>{doc.entityName}</td>
                  <td>{doc.entityType}</td>
                  <td>{doc.documentNumber}</td>
                  <td>{doc.lastUpdated}</td>
                  <td>{doc.validTill}</td>
                  <td>
                    <span className={`status-badge ${doc.status.toLowerCase().replace(' ', '-')}`}>
                      {doc.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn view-btn" title="View" onClick={() => handleView(doc)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 3C4.5 3 1.73 5.11 0 8C1.73 10.89 4.5 13 8 13C11.5 13 14.27 10.89 16 8C14.27 5.11 11.5 3 8 3ZM8 11C6.34 11 5 9.66 5 8C5 6.34 6.34 5 8 5C9.66 5 11 6.34 11 8C11 9.66 9.66 11 8 11ZM8 6.5C7.17 6.5 6.5 7.17 6.5 8C6.5 8.83 7.17 9.5 8 9.5C8.83 9.5 9.5 8.83 9.5 8C9.5 7.17 8.83 6.5 8 6.5Z" fill="#2563EB"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="document-pagination">
        <div className="pagination-info">
          Showing {startIndex + 1} to {Math.min(endIndex, filteredDocuments.length)} of {filteredDocuments.length} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      </div>
      </>
      )}

      {/* Replace Document Confirmation Modal */}
      {showReplaceConfirm && (
        <div className="modal-overlay replace-confirm-overlay" onClick={handleReplaceCancel}>
          <div className="delete-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-vehicle-header">
              <div className="delete-warning-icon">⚠️</div>
              <h2 className="delete-vehicle-title">Confirm Action</h2>
            </div>
            <div className="delete-vehicle-body">
              <div className="delete-vehicle-message">
                Are you sure you want to replace this document? This action will permanently remove the existing document.
              </div>
              <div className="delete-vehicle-details">
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Document Type:</span>
                  <span className="delete-detail-value">{replacingDocument?.documentType}</span>
                </div>
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Entity Name:</span>
                  <span className="delete-detail-value">{replacingDocument?.entityName}</span>
                </div>
              </div>
            </div>
            <div className="delete-vehicle-footer">
              <button className="delete-vehicle-cancel-btn" onClick={handleReplaceCancel}>
                Cancel
              </button>
              <button className="delete-vehicle-delete-btn" onClick={handleReplaceConfirm}>
                Replace Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Document Success Modal */}
      {showReplaceSuccess && (
        <div className="modal-overlay" onClick={handleReplaceSuccessClose}>
          <div className="replace-success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="replace-success-header">
              <div className="replace-success-icon-wrapper">
                <svg className="replace-success-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                </svg>
              </div>
              <h2 className="replace-success-title">Success</h2>
              <button className="replace-success-close" onClick={handleReplaceSuccessClose}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="replace-success-body">
              <div className="replace-success-message">
                Document has been successfully Replaced
              </div>
              <div className="replace-success-details">
                <div className="replace-success-detail-row">
                  <span className="replace-success-detail-label">Document Type:</span>
                  <span className="replace-success-detail-value">{replacedDocumentType}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Replace Document Modal */}
      {showReplaceModal && replacingDocument && (
        <div className="modal-overlay" onClick={handleCloseReplaceModal}>
          <div className="replace-document-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="replace-document-header">
              <h2 className="replace-document-title">Replace Document - {replacingDocument.documentType}</h2>
              <button className="action-btn" onClick={handleCloseReplaceModal}>✖</button>
            </div>
            <div className="replace-document-body">
              <div className="current-document-info">
                <h3 className="details-section-title">Current Document Information</h3>
                <div className="detail-item">
                  <div className="detail-icon">📄</div>
                  <div className="detail-content">
                    <div className="detail-label">Document Type</div>
                    <div className="detail-value">{replacingDocument.documentType}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-icon">🏷️</div>
                  <div className="detail-content">
                    <div className="detail-label">Entity Type</div>
                    <div className="detail-value">{replacingDocument.entityType}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-icon">📝</div>
                  <div className="detail-content">
                    <div className="detail-label">Entity Name</div>
                    <div className="detail-value">{replacingDocument.entityName}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-icon">📅</div>
                  <div className="detail-content">
                    <div className="detail-label">Last Updated</div>
                    <div className="detail-value">{replacingDocument.lastUpdated.split(',')[0]}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-content">
                    <div className="detail-label">Status</div>
                    <div className="detail-value">
                      <span className={`status-badge ${replacingDocument.status.toLowerCase().replace(' ', '-')}`}>
                        ✓ {replacingDocument.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="current-document-preview">
                  <div className="detail-label" style={{ marginBottom: '8px' }}>Current Document Preview</div>
                  <div className="preview-thumbnail">
                    <img 
                      src={replacingDocument.preview || 'https://via.placeholder.com/200x250/6366F1/FFFFFF?text=Document'} 
                      alt="Current document"
                      className="thumbnail-image"
                    />
                  </div>
                  <div className="current-file-info">
                    <span className="file-icon">📄</span>
                    <span className="file-name">{replacingDocument.fileName || `${replacingDocument.documentType}_${replacingDocument.entityName}.pdf`} ({replacingDocument.fileSize || 'N/A'})</span>
                  </div>
                </div>
              </div>

              <div className="upload-new-document">
                <h3 className="details-section-title">Upload New Document</h3>
                <div
                  className={`upload-area ${isDragging ? 'dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('replace-document-file-input').click()}
                >
                  {newDocumentPreview ? (
                    <div className="uploaded-preview">
                      <img src={newDocumentPreview} alt="New document" className="uploaded-preview-image" />
                      <div className="uploaded-file-name">{newDocumentFile?.name}</div>
                      <button 
                        className="remove-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewDocumentFile(null);
                          setNewDocumentPreview(null);
                        }}
                      >
                        ✖
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="upload-icon">☁️</div>
                      <div className="upload-text">Drop your new document here or click to browse files</div>
                      <div className="upload-file-types">
                        <span className="file-type-badge">📄 PDF</span>
                        <span className="file-type-badge">🖼️ PNG</span>
                        <span className="file-type-badge">🖼️ JPG</span>
                      </div>
                      <div className="upload-size-limit">Maximum size: 5MB</div>
                    </>
                  )}
                  <input
                    id="replace-document-file-input"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </div>

                <div className="replace-warning">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-message">Replacing this file will permanently remove the existing document.</div>
                </div>

                <div className="date-field">
                  <label className="date-label">
                    <span className="date-icon">📅</span>
                    Expiry Date
                  </label>
                  <div className="date-input-wrapper">
                    <input
                      type="date"
                      className="date-input"
                      value={expiryDate || ''}
                      onChange={(e) => {
                        setExpiryDate(e.target.value);
                      }}
                    />
                    <span className="date-icon-input">📅</span>
                  </div>
                </div>

                <button 
                  className="replace-save-btn"
                  onClick={handleReplaceAndSave}
                  disabled={!newDocumentFile}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 6L5 9H7V13H9V9H11L8 6Z" fill="currentColor"/>
                    <path d="M3 3V5H1V5C1 3.9 1.9 3 3 3H3Z" fill="currentColor"/>
                    <path d="M13 3C14.1 3 15 3.9 15 5V5H13V3H13Z" fill="currentColor"/>
                  </svg>
                  Replace & Save
                </button>
              </div>
            </div>
            <div className="replace-document-footer">
              <button className="btn-secondary" onClick={handleCloseReplaceModal}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {showViewModal && viewingDocument && (
        <div className="modal-overlay" onClick={handleCloseViewModal}>
          <div className="view-document-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="view-document-header">
              <h2 className="view-document-title">View Document - {viewingDocument.documentType}</h2>
              <button className="action-btn" onClick={handleCloseViewModal}>✖</button>
            </div>
            <div className="view-document-body">
              <div className="view-document-details">
                <h3 className="details-section-title">Document Details</h3>
                <div className="detail-item">
                  <div className="detail-icon">📄</div>
                  <div className="detail-content">
                    <div className="detail-label">Document Type</div>
                    <div className="detail-value">{viewingDocument.documentType}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-icon">🏷️</div>
                  <div className="detail-content">
                    <div className="detail-label">Entity Type</div>
                    <div className="detail-value">{viewingDocument.entityType}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-icon">📝</div>
                  <div className="detail-content">
                    <div className="detail-label">Entity Name</div>
                    <div className="detail-value">{viewingDocument.entityName}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-content">
                    <div className="detail-label">Document Number</div>
                    <div className="detail-value">{viewingDocument.documentNumber}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-icon">📅</div>
                  <div className="detail-content">
                    <div className="detail-label">Expiry Date</div>
                    <div className="detail-value">{viewingDocument.validTill}</div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-content">
                    <div className="detail-label">Status</div>
                    <div className="detail-value">
                      <span className={`status-badge ${viewingDocument.status.toLowerCase().replace(' ', '-')}`}>
                        ✓ {viewingDocument.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-content">
                    <div className="detail-label">Last Updated</div>
                    <div className="detail-value">{viewingDocument.lastUpdated}</div>
                  </div>
                </div>
              </div>

              <div className="view-document-preview">
                <div className="preview-controls">
                  <button className="zoom-btn" onClick={handleZoomOut} disabled={zoomLevel <= 50}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <span className="zoom-level">{zoomLevel}%</span>
                  <button className="zoom-btn" onClick={handleZoomIn} disabled={zoomLevel >= 200}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 5V15M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button className="fullscreen-btn" onClick={handleFullscreen}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 2H2V6M14 2H18V6M18 14V18H14M6 18H2V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
                <div className="preview-container">
                  <div className="preview-image-wrapper" style={{ transform: `scale(${zoomLevel / 100})` }}>
                    {viewingDocument.preview && viewingDocument.preview.startsWith('data:') ? (
                      <img
                        src={viewingDocument.preview}
                        alt={viewingDocument.documentType}
                        className="preview-image"
                      />
                    ) : viewingDocument.preview && viewingDocument.preview.includes('.pdf') ? (
                      <iframe
                        src={viewingDocument.preview}
                        className="preview-image"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title={viewingDocument.documentType}
                      />
                    ) : (
                      <img
                        src={viewingDocument.preview || 'https://via.placeholder.com/800x1000/6366F1/FFFFFF?text=Document+Preview'}
                        alt={viewingDocument.documentType}
                        className="preview-image"
                      />
                    )}
                  </div>
                </div>
                <div className="preview-footer">
                  <div className="file-info">
                    <span className="file-icon">📄</span>
                    <span className="file-name">{viewingDocument.fileName || `${viewingDocument.documentType}_${viewingDocument.entityName}.pdf`} ({viewingDocument.fileSize || 'N/A'})</span>
                  </div>
                  <div className="upload-date">
                    Uploaded on {viewingDocument.uploadDate || viewingDocument.lastUpdated.split(',')[0]}
                  </div>
                </div>
              </div>
            </div>
            <div className="view-document-footer" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
              <button 
                className="btn-secondary" 
                onClick={() => handleDownload(viewingDocument)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  minWidth: '140px',
                  justifyContent: 'center'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 10L5 7H7V3H9V7H11L8 10Z" fill="currentColor"/>
                  <path d="M3 11V13H13V11H15V13C15 14.1 14.1 15 13 15H3C1.9 15 1 14.1 1 13V11H3Z" fill="currentColor"/>
                </svg>
                Download
              </button>
              <button 
                className="btn-secondary" 
                onClick={handleReplaceDocument}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  minWidth: '140px',
                  justifyContent: 'center'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6L5 9H7V13H9V9H11L8 6Z" fill="currentColor"/>
                  <path d="M3 3V5H1V5C1 3.9 1.9 3 3 3H3Z" fill="currentColor"/>
                  <path d="M13 3C14.1 3 15 3.9 15 5V5H13V3H13Z" fill="currentColor"/>
                </svg>
                Replace Document
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCloseViewModal}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  minWidth: '100px',
                  justifyContent: 'center'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement;
